<template>
  <div class="draw-freehand-map">
    <!-- The map-container now has no vertical margins to allow for info box above -->
    <div class="map-container">
      <GoogleMap ref="mapRef" :center="center" :zoom="7" style="width: 100%; height: 600px;"
        :options="{ clickableIcons: false, gestureHandling: 'greedy' }" @click="handleMapClick">
        <!-- Display the user-drawn polygon if we have at least 3 points -->
        <Polygon v-if="points.length >= 3" 
        :paths="points" 
        :options="{
          fillColor: '#4285F4',
          fillOpacity: 0.35,
          strokeColor: '#4285F4',
          strokeWeight: 2
        }" />
        
        <!-- Display the hunt boundary polygon if available -->
        <Polygon v-if="huntBoundaryPaths.length > 0 && huntBoundaryPaths[0].length >= 3"
        :paths="huntBoundaryPaths"
        :options="{
          fillColor: '#FF5722',
          fillOpacity: 0.35,
          strokeColor: '#FF5722',
          strokeWeight: 2
        }" />

        <!-- Display the drawing line -->
        <Polyline v-if="points.length > 0" :path="points" :options="{
          strokeColor: '#4285F4',
          strokeWeight: 2
        }" />

        <!-- Display markers for each point -->
        <Marker v-for="(point, index) in points" :key="index" :position="point" :options="{
          icon: {
            // A small diamond shape, for example
            path: 'M 0 -5 L 5 0 L 0 5 L -5 0 z',
            scale: 0.75,
            strokeColor: '#FF0000',
            strokeWeight: 1,
            fillColor: '#FF0000',
            fillOpacity: 1
          },
          draggable: true
        }" @dragend="handleMarkerDragEnd($event, index)" />
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Map as GoogleMap, Polygon, Polyline, Marker } from '@fawmi/vue-google-maps'

export default defineComponent({
  name: 'DrawFreehandMap',
  components: {
    GoogleMap,
    Polygon,
    Polyline,
    Marker
  },
  data() {
    return {
      // Center coordinates for Utah (approximately Salt Lake City)
      center: { lat: 39.3199, lng: -111.0937 },
      points: [], // Array of lat/lng points for user-drawn polygon
      huntBoundaryPaths: [], // Array of arrays of lat/lng points for hunt boundary (outer ring + holes)
      polygonKey: 0
    }
  },
  computed: {
    hasShape() {
      return this.points.length > 0;
    }
  },
  methods: {
    
    startDrawing() {
      this.points = [];

      // Emit that drawing has started
      this.$emit('drawing-started');
    },

    handleMapClick(event) {
 
      const newPosition = event.latLng;

      // Update the point in the points array
      const updatedPoints = [...this.points];
      const index = updatedPoints.length;
      updatedPoints[index] = {
        lat: newPosition.lat(),
        lng: newPosition.lng()
      };

      this.points = updatedPoints;
      // this.polygonKey++;
      // Emit the updated shape whenever a point is added
      this.$emit('shape-updated', {
        type: 'polygon',
        points: this.points
      });
    },

    handleMarkerDragEnd(event, index) {
      // Get the new position of the marker
      const newPosition = event.latLng;

      // Update the point in the points array
      const updatedPoints = [...this.points];
      updatedPoints[index] = {
        lat: newPosition.lat(),
        lng: newPosition.lng()
      };

      this.points = updatedPoints;
      // this.polygonKey++;
      // Emit the updated shape
      this.$emit('shape-updated', {
        type: 'polygon',
        points: this.points
      });

    },

    clearShape() {
      this.points = [];
      this.$emit('shape-cleared');
    },
    
    /**
     * Displays a polygon on the map using the provided paths
     * @param {Array} paths - Array of arrays of {lat, lng} objects. First array is outer boundary, rest are holes.
     */
    displayPolygon(paths) {
      if (!paths || !Array.isArray(paths) || paths.length === 0 || !paths[0] || paths[0].length < 3) {
        this.huntBoundaryPaths = [];
        return;
      }

      this.huntBoundaryPaths = paths;

      // If the map is already loaded, fit bounds to show the polygon
      this.$nextTick(() => {
        const mapInstance = this.$refs.mapRef?.$mapObject;
        if (mapInstance && this.huntBoundaryPaths.length > 0 && this.huntBoundaryPaths[0].length >= 3) {
          // Calculate the center of the polygon using the outer boundary (first path)
          const bounds = new google.maps.LatLngBounds();
          this.huntBoundaryPaths[0].forEach(point => {
            bounds.extend(new google.maps.LatLng(point.lat, point.lng));
          });

          // Get the center of the hunt boundary
          const huntCenter = bounds.getCenter();

          // Optionally set a specific zoom first
          mapInstance.setZoom(6.5);

          // Wait for the map to be idle after zoom change
          google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
            // Get the map's height in pixels
            const mapHeight = mapInstance.getDiv().offsetHeight;

            // We want the center to appear 30% from the top
            const desiredPositionFromTop = mapHeight * 0.3;

            // Offset needed from the true center (50% from top) to 30% from top
            const offset = (mapHeight / 2) - desiredPositionFromTop;

            // Convert that offset in pixels to an offset in latitude
            const projection = mapInstance.getProjection();
            if (projection) {
              const scale = Math.pow(2, mapInstance.getZoom());

              // Convert the hunt boundary center to "world" coords
              const worldCenter = projection.fromLatLngToPoint(huntCenter);

              // Convert our pixel offset to the same scale as "world" coordinates
              const pixelOffsetInWorldCoords = offset / scale;

              // Create a new point with the adjusted y value
              const adjustedWorldPoint = new google.maps.Point(
                worldCenter.x,
                worldCenter.y + pixelOffsetInWorldCoords
              );

              // Convert back to a LatLng and set the map center
              const adjustedLatLng = projection.fromPointToLatLng(adjustedWorldPoint);
              mapInstance.setCenter(adjustedLatLng);
            } else {
              // Fallback if no projection is available
              mapInstance.setCenter(huntCenter);
            }
          });

          // Initial center (will be adjusted after 'idle')
          mapInstance.setCenter(huntCenter);
        }
      });
    }
  }
})
</script>

<style scoped>
.draw-freehand-map {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
}

.controls {
  padding: 1rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.instructions {
  text-align: center;
  font-size: 0.9rem;
  color: #333;
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

button {
  padding: 0.5rem 1rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

button:hover:not(:disabled) {
  background-color: #45a049;
}

.map-container {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}
</style>
